import { Info, PhoneCall } from 'lucide-react';
import { clsx } from 'clsx';
import { Button, Callout } from '@/shared/ui';
import patterns from '@/shared/ui/styles/patterns.module.css';
import type { CompletenessSummary } from '../api/verificationApi';
import { confidencePct } from '../utils/format';
import { TierChip } from './chips';
import styles from './StillNeededTab.module.css';

interface StillNeededTabProps {
  completeness: CompletenessSummary | null;
  onQueueCall: () => void;
  queueing: boolean;
}

function requiredReason(reason: 'missing' | 'low_confidence' | 'unreviewed', confidence: number) {
  switch (reason) {
    case 'low_confidence':
      return `Low confidence (${confidencePct(confidence)}%) · required`;
    case 'unreviewed':
      return 'Not yet reviewed · required';
    default:
      return 'Missing · required · will be asked on the payer call';
  }
}

export function StillNeededTab({ completeness, onQueueCall, queueing }: StillNeededTabProps) {
  const required = completeness?.unmetRequired ?? [];
  const optional = completeness?.unmetOptional ?? [];

  if (!completeness) {
    return (
      <Callout variant="neutral" icon={Info}>
        Completeness hasn't been evaluated yet. It's calculated once the first check finishes.
      </Callout>
    );
  }

  if (required.length === 0 && optional.length === 0) {
    return (
      <Callout variant="success">
        Every required detail is verified. Nothing is queued for a payer call.
      </Callout>
    );
  }

  return (
    <div className={styles.root}>
      {required.length > 0 ? (
        <Callout variant="warning">
          These are the required fields the electronic check couldn't answer. This exact list is
          what the calling agent is told to ask the payer — nothing more.
        </Callout>
      ) : (
        <Callout variant="success">
          All required details are verified. The items below are optional and don't block
          sign-off.
        </Callout>
      )}

      <div className={clsx(patterns.panel, styles.list)}>
        {required.map((item) => (
          <div key={item.id} className={styles.row}>
            <div className={clsx(styles.icon, styles.iconWarning)}>
              <PhoneCall size={15} aria-hidden />
            </div>
            <div className={styles.text}>
              <div className={styles.question}>{item.label}</div>
              <div className={styles.reason}>{requiredReason(item.reason, item.confidence)}</div>
            </div>
            <TierChip tier={item.tier} />
          </div>
        ))}
        {optional.map((item) => (
          <div key={item.id} className={clsx(styles.row, styles.rowOptional)}>
            <div className={clsx(styles.icon, styles.iconMuted)}>
              <Info size={15} aria-hidden />
            </div>
            <div className={styles.text}>
              <div className={styles.question}>{item.label}</div>
              <div className={styles.reason}>Optional · not blocking sign-off</div>
            </div>
            <span className={patterns.pillMuted}>Optional</span>
          </div>
        ))}
      </div>

      {required.length > 0 && (
        <div className={styles.actions}>
          <Button onClick={onQueueCall} isLoading={queueing}>
            Queue payer call
          </Button>
        </div>
      )}
    </div>
  );
}
